import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { logError } from '@/lib/sentry';
import { fetchInventoryOverview } from '@/supabase/inventory/service/inventoryService';
import type { InventoryOverviewItem, UnmatchedOrderItem } from '@/supabase/inventory/types/inventory';

export interface UseInventoryResult {
  // PR3: InventoryItem → InventoryOverviewItem(+ reserved). 기존 소비처(Inventory.tsx →
  // InventoryTable/InventoryRow)는 InventoryItem 필드를 구조적으로 그대로 갖고 있는 상위집합이라
  // 무수정 컴파일된다.
  items: InventoryOverviewItem[];
  isLoading: boolean;
  // 초기 로드 실패 감지용(codexloop R1 Fix D, 판정 기준은 PR3 R1 #3에서 hasLoadedRef로 변경)
  // — 성공 로드가 한 번도 없는 상태의 실패만 true(첫 로드 실패를 빈 목록과 구분). 성공 로드
  // 이후의 refresh 실패는 화면(목록/미매칭 패널) 이전 값 보존, hasError는 건드리지 않는다
  // (조용한 재조회 실패로 화면을 오류 상태로 뒤집으면 회귀).
  hasError: boolean;
  refresh: () => Promise<void>;
  // PR3 파생 예약: overview RPC가 함께 내려주는 나머지 필드. items와 동일한 seq/락/보존 규율로
  // 갱신된다(성공 시 통째로 교체, 실패 시 손대지 않고 이전 값 보존 — items 보존과 대칭).
  unmatched: UnmatchedOrderItem[];
  unmatchedTotal: number;
  scanTruncated: boolean;
  windowDays: number;
}

/**
 * 재고 상품 목록 도메인 훅(PR2 Task2-A, PR3에서 overview로 전환). fetchInventoryOverview()는
 * 인자가 없어(RLS가 user_id로 스코프, 윈도우는 RPC 디폴트 90) Props 인터페이스는 두지 않는다
 * — 빈 Props는 순수 보일러플레이트라 생략.
 *
 * mount 시 1회 자동 fetch(언마운트 가드 = mountedRef, setup에서 true로 리셋 — StrictMode
 * setup→cleanup→setup 재마운트에도 가드가 영구 false로 굳지 않도록), refresh()는 액션 후
 * 재조회용으로 재사용. 성공 로드 전(hasLoadedRef false)에만 isLoading을 true로 올리고,
 * 성공 로드 후의 refresh()는 스피너 없이 조용히 교체.
 * 실패 시 toast만, 기존 items는 보존(useDeposits 컨벤션 — 뮤테이션 성공 토스트 직후 재조회가
 * 실패했다고 화면을 비우면 회귀). unmatched/unmatchedTotal/scanTruncated/windowDays도 동일 원칙
 * — 실패 분기에서는 건드리지 않아 이전 성공 시점 값을 그대로 유지한다.
 * 동시 refresh() 보호는 skip 가드가 아니라 latest-wins seq 토큰(refreshSeqRef) — refresh()는
 * 뮤테이션 성공 후 onMutated로도 불리므로, 부르는 즉시 skip하면 그 호출이 드롭되어 리스트가
 * stale로 남는다(useInventoryLedger의 isFetchingRef skip 가드와 의도적으로 다름). 매 호출이
 * fetch는 하되, 그 사이 더 최신 호출이 시작됐으면 자신의 결과 반영/토스트/isLoading 해제를 건너뛴다.
 *
 * isLoading/hasError 진입 조건은 "성공 로드가 한 번이라도 있었는가"(hasLoadedRef,
 * codexloop PR3 R1 #3 수용) — PR2 시절의 itemsRef.length === 0 판정은 overview 전환 후
 * 오판정이 생긴다: 마스터 0개 + 미매칭 N개(온보딩 상태)로 성공 로드된 화면에서 background
 * refresh가 한 번 실패하면 목록이 비었다는 이유로 오류 화면으로 뒤집혔다. hasLoadedRef는
 * 성공 시에만 true로 굳으므로 "초기 로드 실패 후 재시도"는 여전히 blocking(스피너)으로
 * 다시 올라가고(PR2 codexloop R2 Fix 4 의도 보존), 한 번이라도 성공한 뒤에는 목록이 비어
 * 있어도 refresh가 non-blocking + 실패 시 이전 값 보존(오류 화면 전환 없음)이다.
 *
 * available(가용 = stock − reserved)은 여기서 state로 만들지 않는다 — 파생값은 소비처가
 * getAvailableStock(item)으로 렌더 시 계산(PR3 제약 #7, 미러 금지).
 */
export function useInventory(): UseInventoryResult {
  const [items, setItems] = useState<InventoryOverviewItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [unmatched, setUnmatched] = useState<UnmatchedOrderItem[]>([]);
  const [unmatchedTotal, setUnmatchedTotal] = useState(0);
  const [scanTruncated, setScanTruncated] = useState(false);
  const [windowDays, setWindowDays] = useState(90);
  const mountedRef = useRef(true);
  const refreshSeqRef = useRef(0);
  // 성공 로드 1회 이상 여부 — blocking isLoading/오류 화면 전환은 이 값이 false일 때만.
  // (refresh는 [] deps 클로저라 최신 판정은 ref로 읽는다.)
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    const seq = ++refreshSeqRef.current;
    // 초기 로드와 에러 재시도(아직 성공 로드 0회) 둘 다 blocking — 성공 로드가 있었던 화면의
    // background refresh만 non-blocking으로 남긴다.
    if (!hasLoadedRef.current) setIsLoading(true);
    try {
      const overview = await fetchInventoryOverview();
      if (!mountedRef.current || seq !== refreshSeqRef.current) return;
      hasLoadedRef.current = true;
      setItems(overview.items);
      setUnmatched(overview.unmatched);
      setUnmatchedTotal(overview.unmatchedTotal);
      setScanTruncated(overview.scanTruncated);
      setWindowDays(overview.windowDays);
      setHasError(false);
    } catch (err) {
      logError('useInventory.refresh', err);
      if (mountedRef.current && seq === refreshSeqRef.current) {
        toast.error('재고 목록을 불러오지 못했습니다');
        // 성공 로드가 한 번이라도 있었으면 화면(목록/패널)은 이전 값 그대로 보존(회귀 방지)
        // — 오류 상태는 성공 로드 0회(첫 로드부터 실패)일 때만 켠다.
        if (!hasLoadedRef.current) setHasError(true);
      }
    } finally {
      if (mountedRef.current && seq === refreshSeqRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { items, isLoading, hasError, refresh, unmatched, unmatchedTotal, scanTruncated, windowDays };
}
